import { Field, IconButton, Modal, ProgressRing, TextInput } from "@carbon6/c6-frontend-design";
import { useEffect, useId, useMemo, useState, useSyncExternalStore, type DragEvent } from "react";
import { ANNOTATION_SET_NAME_MAX_LENGTH } from "@/api/trainingAnnotation";
import { FolderDropZone } from "@/components/dataset/FolderDropZone/FolderDropZone";
import { HatModalHeader } from "@/components/modal";
import { annotationBundleIssueText } from "@/modules/dataset/public";
import { scanDroppedItems, scanFileList } from "@/upload";
import { formatBytes } from "@/utils";
import {
  AnnotationBundleImportController,
  annotationImportBusy,
} from "./AnnotationBundleImportController";
import { AnnotationImportInputError } from "./annotationImportError";
import styles from "./AnnotationBundleImportModal.module.css";

export function AnnotationBundleImportModal({
  datasetUuid,
  cameras,
  onClose,
  onImported,
}: {
  datasetUuid: string;
  cameras: readonly string[];
  onClose(): void;
  onImported(setUuid: string): Promise<boolean>;
}) {
  const cameraKey = JSON.stringify(cameras);
  const controller = useMemo(
    () => new AnnotationBundleImportController(datasetUuid, new Set<string>(JSON.parse(cameraKey))),
    [datasetUuid, cameraKey],
  );
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  useEffect(() => {
    controller.init();
    return () => controller.dispose();
  }, [controller]);
  const folderInputId = useId();
  const [name, setName] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const busy = annotationImportBusy(state.status) || opening;
  const selection = state.selection;
  const blocked =
    selection === null ||
    selection.analysis.issues.length > 0 ||
    selection.rejectedCount > 0 ||
    state.status === "scanning";
  const stopDrag = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const clickFolder = () => {
    if (!busy) document.getElementById(folderInputId)?.click();
  };
  const start = async () => {
    if (busy) return;
    await controller.start(name.trim() || null);
    const result = controller.getSnapshot().result;
    if (result === null) return;
    setOpening(true);
    setOpenError(null);
    try {
      if (await onImported(result.setUuid)) onClose();
    } catch {
      setOpenError("Annotation Set 목록을 갱신하지 못했습니다. 다시 열어 주세요.");
    } finally {
      setOpening(false);
    }
  };
  const completed = state.result !== null;
  return (
    <Modal
      open
      size="md"
      onClose={() => {
        if (!busy) onClose();
      }}
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
    >
      <HatModalHeader title="파일에서 Annotation 가져오기" close={{ disabled: busy }} />
      <div className={styles.body}>
        <p className={styles.caption}>
          카메라 폴더별로 이름이 같은 PNG/YAML 짝을 선택해 주세요. 가져온 번들은 읽기 전용
          Annotation Set으로 등록됩니다.
        </p>
        <FolderDropZone
          folderInputId={folderInputId}
          folderName={selection?.rootName ?? null}
          inputLabel="Annotation 번들 폴더"
          caption={
            selection
              ? `파일 ${selection.analysis.fileCount}개 · ${formatBytes(selection.analysis.totalBytes)}`
              : "Dataset 폴더, random_background 폴더 또는 카메라 폴더들을 담은 루트를 선택해 주세요."
          }
          scanning={state.status === "scanning"}
          dragActive={dragActive}
          disabled={busy || completed}
          onClick={clickFolder}
          onFolderInputChange={(event) => {
            const files = Array.from(event.currentTarget.files ?? []);
            event.currentTarget.value = "";
            if (files.length) void controller.selectFolder(() => scanFileList(files));
          }}
          onDragEnter={(event) => {
            stopDrag(event);
            if (!busy) setDragActive(true);
          }}
          onDragOver={stopDrag}
          onDragLeave={(event) => {
            stopDrag(event);
            setDragActive(false);
          }}
          onDrop={(event) => {
            stopDrag(event);
            if (busy || completed) return;
            setDragActive(false);
            const items = Array.from(event.dataTransfer.items);
            void controller.selectFolder(async () => {
              const result = await scanDroppedItems(items);
              if (result.kind !== "ok")
                throw new AnnotationImportInputError("번들을 담은 폴더 한 개를 선택해 주세요.");
              return result.scan;
            });
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              clickFolder();
            }
          }}
        />
        {selection && (
          <div className={styles.summary}>
            <span>
              keyframe {selection.analysis.keyframeCount ?? "—"}개 · 카메라{" "}
              {selection.analysis.cameras.length}개
            </span>
            <span>
              {selection.analysis.cameras
                .map((camera) => `${camera.camera} ${camera.keyframeCount}개`)
                .join(" · ")}
            </span>
            {selection.analysis.issues.map((issue) => (
              <p key={issue.code} role="alert">
                {annotationBundleIssueText(issue)}
              </p>
            ))}
            {selection.rejectedCount > 0 && (
              <p role="alert">경로가 잘못되었거나 중복된 파일이 있습니다. 폴더를 확인해 주세요.</p>
            )}
          </div>
        )}
        <Field label="Annotation Set 이름">
          <TextInput
            value={name}
            maxLength={ANNOTATION_SET_NAME_MAX_LENGTH}
            disabled={busy || completed}
            placeholder="이름 없음"
            fullWidth
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        {(busy || state.completedFiles > 0) && (
          <div className={styles.progress} role="status">
            <ProgressRing
              label="번들 전송 진행률"
              value={
                state.status === "declaring" || state.status === "finalizing" || opening
                  ? undefined
                  : state.uploadedBytes / (selection?.analysis.totalBytes || 1)
              }
            />
            <span>
              {state.status === "declaring"
                ? "전송을 준비하고 있습니다."
                : state.status === "finalizing"
                  ? "번들을 검증하고 등록하고 있습니다."
                  : opening
                    ? "Annotation Set을 열고 있습니다."
                    : `파일 ${state.completedFiles}/${selection?.analysis.fileCount ?? 0}개 · ${formatBytes(state.uploadedBytes)} 전송됨`}
            </span>
          </div>
        )}
        {(state.error || openError) && (
          <p className={styles.error} role="alert">
            {state.error || openError}
          </p>
        )}
        {state.result && (
          <p role="status">
            {state.result.duplicate
              ? "이미 등록된 같은 번들을 찾았습니다. 해당 Annotation Set을 열 수 있습니다."
              : "번들을 등록했습니다. 가져온 Annotation Set을 열 수 있습니다."}
          </p>
        )}
        {state.result?.warnings.map((warning) => (
          <p key={warning}>{warning}</p>
        ))}
        <div className={styles.actions}>
          <IconButton
            text={completed ? "닫기" : "취소"}
            variant="surface"
            disabled={busy}
            onClick={onClose}
          />
          <IconButton
            text={
              completed
                ? "Annotation Set 열기"
                : state.status === "error"
                  ? "다시 시도"
                  : "가져오기"
            }
            variant="solid"
            disabled={busy || blocked}
            onClick={() => void start()}
          />
        </div>
      </div>
    </Modal>
  );
}
